import { reactive, computed, watchEffect, ref, watch } from 'vue';
import type {
    QuestionProcedure,
    ApiAdapter,
    InteractiveQuestion,
    InteractiveOption,
    FormSection,
    VisibilityOperator,
    OptionType,
    EngineOptions
} from '../types';
import { validateValue, validateFileObject } from '../utils/validation';
import { generateUUID, mapDraftToInitParams } from '../utils/idempotency';
import { PersistenceService } from '../services/persistence';

export function useFormEngine(
    rawSchema: QuestionProcedure[],
    apiAdapter: ApiAdapter,
    options: EngineOptions = { autoFillFromUrl: true, enableAutoSave: true }
) {
    // Estado de Idempotencia y Carga
    const transactionUuid = ref<string>(generateUUID());
    const isDraftLoaded = ref(false);

    // ==========================================
    // 1. PROCESAMIENTO INICIAL DEL SCHEMA
    // ==========================================
    const sortFn = (a: { order: number, id: number }, b: { order: number, id: number }) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.id - b.id;
    };

    const sortedSchema = [...rawSchema].sort(sortFn);

    const processedQuestions: InteractiveQuestion[] = sortedSchema.map(q => {
        const sortedOptions = [...q.questionOption || []].sort(sortFn);
        const interactiveOptions = sortedOptions.map(opt => ({
            ...opt,
            value: null,
            error: null,
            isLoading: false,
            isVisible: true
        }));

        return {
            ...q,
            isVisible: !q.is_hidden,
            isValid: !q.is_required,
            error: null,
            options: interactiveOptions,
            optionGroups: {} as Record<OptionType, InteractiveOption[]>
        };
    });

    const questions = reactive<InteractiveQuestion[]>(processedQuestions);
    const loading = reactive({ value: false });
    const globalError = reactive({ value: null as string | null });

    // ==========================================
    // 2. SECCIONES (COMPUTED)
    // ==========================================
    const currentSectionIndex = ref(0);

    const sections = computed<FormSection[]>(() => {
        const groups: Record<string, InteractiveQuestion[]> = {};
        const sectionOrder: string[] = [];

        questions.forEach(q => {
            if (!q.isVisible) return;

            let groupId = q.section_name ?? `__orphan__${q.id}`;

            if (!groups[groupId]) {
                groups[groupId] = [];
                sectionOrder.push(groupId);
            }
            groups[groupId].push(q);
        });

        return sectionOrder.map(groupId => {
            const finalTitle = !groupId.includes('__orphan__') && groups[groupId][0]?.show_section_name ? groupId : null
            return {
                id: groupId,
                title: finalTitle,
                questions: groups[groupId]
            };
        });
    });

    const currentSection = computed(() => sections.value[currentSectionIndex.value] || null);
    const isFirstStep = computed(() => currentSectionIndex.value === 0);
    const isLastStep = computed(() => currentSectionIndex.value === sections.value.length - 1);

    const isCurrentSectionValid = computed(() => {
        const section = currentSection.value;
        if (!section) return true;
        return section.questions.every(q => q.isValid);
    });

    // ==========================================
    // 3. SETTERS & ACTIONS
    // ==========================================
    const setAnswer = (questionId: number, optionId: number, newValue: string | null, dynamicPrice?: number) => {
        const question = questions.find(q => q.id === questionId);
        if (!question) return;

        if (!question.allow_multiple_answers) {
            question.options.forEach(opt => {
                if (opt.id !== optionId) {
                    opt.value = null;
                    opt.error = null;
                }
            });
        }

        const option = question.options.find(o => o.id === optionId);
        if (option) {
            option.value = newValue === "" ? null : newValue;
            option.error = validateValue(option.value, option.type, option.validation_rules);
            if (dynamicPrice !== undefined) {
                option.price_modifier = dynamicPrice;
            }
        }
    };

    const handleFileUpload = async (questionId: number, optionId: number, file: File) => {
        const q = questions.find(qq => qq.id === questionId);
        const opt = q?.options.find(o => o.id === optionId);
        if (!q || !opt) return "Error interno: Opción no encontrada";

        const fileError = validateFileObject(file, opt.validation_rules);
        if (fileError) {
            opt.error = fileError;
            return fileError;
        }

        opt.isLoading = true;
        q.isValid = false;
        opt.error = null;

        try {
            const { url } = await apiAdapter.uploadFile(file);
            setAnswer(questionId, optionId, url);
            return null;
        } catch (e) {
            console.error(e);
            opt.error = "Error al subir archivo";
            return opt.error;
        } finally {
            opt.isLoading = false;
            const stillLoading = q.options.some(o => o.isLoading);
            if (!stillLoading) {
                q.isValid = true;
            }
        }
    };

    // ==========================================
    // 4. MOTOR DE REGLAS (WatchEffect)
    // ==========================================
    watchEffect(() => {
        const checkCondition = (condition: any, contextQuestion?: InteractiveQuestion) => {
            if (!condition) return true;

            const { target_question_procedure_id, operator, target_option_id } = condition;
            let targetQ: InteractiveQuestion | undefined;

            if (target_question_procedure_id) {
                targetQ = questions.find(q => q.id === target_question_procedure_id);
            } else {
                targetQ = contextQuestion;
            }

            if (!targetQ || !targetQ.isVisible) return false;

            const selectedIds = targetQ.options
                .filter(opt => opt.value !== null && opt.value !== undefined && opt.value !== "")
                .map(opt => opt.id);

            switch (operator as VisibilityOperator) {
                case 'includes_option':
                    return target_option_id ? selectedIds.some(id => String(id) === String(target_option_id)) : false;
                case 'excludes_option':
                    return target_option_id ? !selectedIds.some(id => String(id) === String(target_option_id)) : true;
                case 'is_empty':
                    return selectedIds.length === 0;
                case 'is_not_empty':
                    return selectedIds.length > 0;
                default:
                    return true;
            }
        };

        questions.forEach(q => {
            // Visibilidad Pregunta
            let isQVisible = true;
            if (q.visibility_condition) {
                isQVisible = checkCondition(q.visibility_condition);
            } else if (q.hide_if_prefilled) {
                const hasValue = q.options.some(o => o.value !== null);
                if (hasValue) isQVisible = false;
            } else {
                isQVisible = !q.is_hidden;
            }

            if (q.isVisible && !isQVisible) {
                q.options.forEach(o => o.value = null);
            }
            q.isVisible = isQVisible;

            // Visibilidad Opciones y Grupos
            const newGroups: Record<OptionType, InteractiveOption[]> = {} as any;
            q.options.forEach(opt => {
                let isOptVisible = true;
                if (q.isVisible && opt.visibility_condition) {
                    isOptVisible = checkCondition(opt.visibility_condition, q);
                }

                if (opt.isVisible && !isOptVisible) {
                    opt.value = null;
                }
                opt.isVisible = isOptVisible;

                if (isOptVisible) {
                    if (!newGroups[opt.type]) newGroups[opt.type] = [];
                    newGroups[opt.type].push(opt);
                }
            });
            q.optionGroups = newGroups;
        });

        // Validación
        questions.forEach(q => {
            // si no es visble no se valida
            if (!q.isVisible) {
                q.isValid = true;
                q.error = null;
                return;
            }

            q.options.forEach(opt => {
                if (!opt.isVisible) {
                    opt.error = null;
                    return;
                }
                if (!opt.error && opt.is_required) {
                    const isEmpty = opt.value === null || opt.value === "" || opt.value === undefined;
                    if (isEmpty) {
                        opt.error = "Esta opción es obligatoria.";
                    }
                }
            });

            const hasOptionErrors = q.options.some(opt => opt.isVisible && opt.error !== null);
            if (hasOptionErrors) {
                q.isValid = false;
                q.error = null;
                return;
            }
            if (q.is_required) {
                const hasValue = q.options.some(opt => opt.isVisible && opt.value !== null && opt.value !== "");
                if (!hasValue) {
                    q.isValid = false;
                    q.error = "Este campo es requerido.";
                } else {
                    q.isValid = true;
                    q.error = null;
                }
            } else {
                q.isValid = true;
                q.error = null;
            }
        });
    });

    // ==========================================
    // 5. HELPER LOGIC (Include, Payload, Price)
    // ==========================================

    // Determina si una pregunta se incluye en el payload/precio (Visible O (Oculta por prellenado Y con valor))
    const shouldInclude = (q: InteractiveQuestion) => {
        const hasValue = q.options.some(opt => opt.value !== null && opt.value !== "");
        if (q.isVisible) return true;
        if (q.hide_if_prefilled && hasValue) return true;
        if (q.is_hidden && hasValue) return true; // Descomentar si se desea enviar hidden fields con valor(solo se contestan por url, jamas se muestran)
        return false;
    };

    const isFormValid = computed(() => questions.filter(q => q.isVisible).every(q => q.isValid));

    const totalPrice = computed(() => {
        let total = options.basePrice || 0;
        questions.forEach(q => {
            if (shouldInclude(q)) {
                q.options.forEach(opt => {
                    if (opt.value !== null && opt.value !== "") {
                        total += Number(opt.price_modifier || 0);
                    }
                });
            }
        });
        return total;
    });

    // Función INIT para rellenar respuestas (Formato ID o Título o Separador '::')
    const init = (params: Record<string, string | string[]>) => {
        const SEPARATOR = '::';

        Object.entries(params).forEach(([key, val]) => {
            if (!val) return;
            const values = Array.isArray(val) ? val : [val];

            values.forEach(singleValue => {
                if (!singleValue) return;

                // 1. Buscar Pregunta
                const q = questions.find(q =>
                    String(q.id) === key ||
                    q.name === key
                );
                if (!q) return;

                // 2. Formato explícito "ID_OPCION::VALOR_CUSTOM"
                if (singleValue.includes(SEPARATOR)) {
                    const [targetIdentifier, customAnswer] = singleValue.split(SEPARATOR);

                    // Buscar por ID
                    let specificOption = q.options.find(o => String(o.id) === targetIdentifier);
                    // Si no, buscar por Título
                    if (!specificOption) {
                        specificOption = q.options.find(o => o.title.toLowerCase() === targetIdentifier.toLowerCase());
                    }

                    if (specificOption) {
                        setAnswer(q.id, specificOption.id, customAnswer);
                        if (q.hide_if_prefilled) q.isVisible = false;
                        return;
                    }
                    return;
                }

                // 3. Fallback: Buscar por ID exacto
                const targetOptionById = q.options.find(o => String(o.id) === singleValue);
                if (targetOptionById) {
                    setAnswer(q.id, targetOptionById.id, targetOptionById.title);
                } else {
                    // 4. Fallback: Buscar por Título exacto
                    const targetOptionByTitle = q.options.find(o => o.title.toLowerCase() === singleValue.toLowerCase());
                    if (targetOptionByTitle) {
                        setAnswer(q.id, targetOptionByTitle.id, targetOptionByTitle.title);
                    }
                }

                if (q.hide_if_prefilled && q.options.some(o => o.value !== null)) {
                    q.isVisible = false;
                }
            });
        });
    };

    const getPayload = () => {
        const answeredQuestions = questions.filter(q => shouldInclude(q));
        return answeredQuestions.map(q => ({
            question: q.question.id,
            question_option_history: q.options
                .filter(opt => opt.value !== null && opt.value !== "")
                .map(opt => ({ questionOption: opt.id, answer: opt.value as string }))
        })).filter(q => q.question_option_history.length > 0);
    };

    // ==========================================
    // 6. PERSISTENCIA & INICIALIZACIÓN
    // ==========================================

    const checkUrlParams = () => {
        if (typeof window === 'undefined') return;
        let searchParams = new URLSearchParams(window.location.search);

        if (Array.from(searchParams.toString()).length === 0 && window.location.hash.includes('?')) {
            searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
        }

        const paramsFromUrl: Record<string, string | string[]> = {};
        searchParams.forEach((value, key) => {
            if (paramsFromUrl[key]) {
                if (Array.isArray(paramsFromUrl[key])) {
                    (paramsFromUrl[key] as string[]).push(value);
                } else {
                    paramsFromUrl[key] = [paramsFromUrl[key] as string, value];
                }
            } else {
                paramsFromUrl[key] = value;
            }
        });

        if (Object.keys(paramsFromUrl).length > 0) {
            init(paramsFromUrl);
        }
    };

    const initializeEngine = async () => {
        // Si hay ID de trámite y autosave, intentamos cargar borrador
        if (options.procedureId && options.enableAutoSave) {
            const draft = await PersistenceService.load(options.procedureId);

            if (draft) {
                transactionUuid.value = draft.uuid;
                const initParams = mapDraftToInitParams(draft);
                init(initParams);
                isDraftLoaded.value = true;
            } else {
                if (options.autoFillFromUrl) checkUrlParams();
            }

            // Auto-Guardado
            watch(
                [() => questions, () => totalPrice.value],
                async () => {
                    if (loading.value) return;

                    // -------------------------------------------------------------
                    // CORRECCIÓN: Generamos un payload ESPECÍFICO para el borrador
                    // No usamos getPayload() porque ese es para el Backend.
                    // Aquí necesitamos 'id' y 'answers' para que coincida con el mapper.
                    // -------------------------------------------------------------
                    const draftPayload = questions
                        .filter(q => shouldInclude(q))
                        .map(q => ({
                            id: q.id, // Guardamos ID 10 (ProcedureQuestion), no 173 (Question)
                            answers: q.options
                                .filter(opt => opt.value !== null && opt.value !== "")
                                .map(opt => ({
                                    id: opt.id,
                                    value: opt.value as string
                                }))
                        }));

                    const hasAnswers = draftPayload.length > 0;

                    if (hasAnswers && options.procedureId) {
                        await PersistenceService.save(options.procedureId, {
                            uuid: transactionUuid.value,
                            timestamp: Date.now(),
                            payload: { questions: draftPayload } // Guardamos draftPayload
                        });
                    }
                },
                { deep: true }
            );
        } else {
            // Modo simple sin persistencia
            if (options.autoFillFromUrl) checkUrlParams();
        }
    };

    // Iniciamos el motor
    initializeEngine();

    // ==========================================
    // 7. PUBLIC API & SUBMIT
    // ==========================================

    const submit = async (extraData: any) => {
        if (!isFormValid.value) {
            globalError.value = "Hay errores en el formulario.";
            return;
        }
        loading.value = true;
        globalError.value = null;
        try {
            const fullPayload = {
                ...extraData,
                questions: getPayload(),
                uuid: transactionUuid.value, // Envia el UUID (ref.value)
            };

            const result = await apiAdapter.submitProcedure(fullPayload);

            // Si fue exitoso y usamos persistencia, limpiamos el borrador
            if (options.procedureId && options.enableAutoSave) {
                await PersistenceService.clear(options.procedureId);
            }

            return result;
        } catch (e: any) {
            globalError.value = e.message || "Error al enviar";
            throw e;
        } finally {
            loading.value = false;
        }
    };

    const setExternalErrors = (errors: Array<{ questionId: number, error: string }>) => {
        errors.forEach(err => {
            const q = questions.find(q => q.id === err.questionId || q.question.id === err.questionId);
            if (q) {
                q.isValid = false;
                q.error = err.error;
            }
        });
    };

    const nextStep = () => { if (!isLastStep.value && isCurrentSectionValid.value) currentSectionIndex.value++; };
    const prevStep = () => { if (!isFirstStep.value) currentSectionIndex.value--; };
    const goToStep = (i: number) => { if (i >= 0 && i < sections.value.length) currentSectionIndex.value = i; };

    return {
        questions,
        sections,
        currentSection,
        currentSectionIndex,
        isFirstStep,
        isLastStep,
        isCurrentSectionValid,
        totalPrice,
        isFormValid,
        loading,
        globalError,
        transactionUuid,
        isDraftLoaded,
        apiAdapter,
        init,
        setAnswer,
        handleFileUpload,
        submit,
        getPayload,
        setExternalErrors,
        nextStep,
        prevStep,
        goToStep
    };
}